import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import Grid from '@mui/material/Grid2';
import { useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";

// Define BeforeInstallPromptEvent type
interface BeforeInstallPromptEvent extends Event {
    prompt: () => void;
    userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const PWAInstallPrompt = () => {
    const { t } = useTranslation();
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    const [showPrompt, setShowPrompt] = useState(false);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    // this runs only once when the component is mounted
    // to ensure event listeners are registered before painting
    useLayoutEffect(() => {
        if (isIOS) {
            //if not yet installed on ios as standalone, we show prompt
            const isStandalone = window.matchMedia("(display-mode: standalone)").matches;
            setShowPrompt(!isStandalone);
            return;
        }

        // Handle beforeinstallprompt event (Android & Windows)
        // this event is fired when the browser is ready to prompt the user to install the PWA
        const handleBeforeInstallPrompt = (e: Event) => {
            const promptEvent = e as BeforeInstallPromptEvent;
            promptEvent.preventDefault(); // Prevent default prompt
            setDeferredPrompt(promptEvent);
            setShowPrompt(true);
        };

        window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt as EventListener);

        return () => {
            window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
        };
    }, [isIOS]);

    const handleInstallClick = () => {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            deferredPrompt.userChoice.then((choiceResult) => {
                setDeferredPrompt(null);
                setShowPrompt(false);
            });
        }
    };

    const handleClose = () => {
        setShowPrompt(false);
    }

    return <Dialog open={showPrompt} onClose={handleClose}>
        <DialogTitle>{t('install_app.title')}</DialogTitle>
        <DialogContent>
            <Typography>
                {isIOS ? t('install_app.instruction_ios') : t('install_app.instruction')}
            </Typography>
        </DialogContent>
        <DialogActions>
            {isIOS ? (
                <Button onClick={handleClose} color="primary">
                    {t('install_app.ack_btn')}
                </Button>
            ) : (
                <Grid container width={'100%'} spacing={2} justifyContent="center" alignItems="center">
                    <Grid>
                        <Button onClick={handleClose} color="secondary">
                            {t('install_app.cancel_btn')}
                        </Button>
                    </Grid>
                    <Grid>
                        <Button onClick={handleInstallClick} color="primary" variant="contained">
                            {t('install_app.install_btn')}
                        </Button>
                    </Grid>
                </Grid>
            )}
        </DialogActions>
    </Dialog>
};

export default PWAInstallPrompt;
